import {
  hiddenLineIds,
  isLineExpandable,
  isLineExpanded,
  lineNodeIds,
  withLineExpandable,
  withLineExpanded
} from 'src/components/star-grid/utils/lineSetExpansion';
import { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { StudioLineEntry, StudioLineSet } from 'src/components/star-grid/reportStudio.types';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { moveStudioItem } from 'src/components/star-grid/utils/studioDraft';
/** The deck's own statement rows sit at 1-3; deeper just pushes the label out of the column. */
const MAX_LINE_INDENT = 4;
export const useStudioLineSets = (state: ReportStudioState) => {
  const [editing, setEditing] = useState<{ setId: string; lineId: string }>();
  // A separate cursor for the member picker: editing a member line goes straight to selecting its
  // records from the dimension hierarchy, not through the generic line-settings modal.
  const [picking, setPicking] = useState<{ setId: string; lineId: string }>();
  /**
   * Snapshots of line sets as they were BEFORE each edit, newest last.
   *
   * A ref, not state: Undo reads it on click and nothing renders from it, so a snapshot must not cause
   * a re-render (which would itself be mistaken for an edit). Capped, because the studio holds a draft
   * in memory for as long as the page is open.
   */
  const history = useRef<StudioLineSet[]>([]);
  const commit = (before: StudioLineSet, next: StudioLineSet): void => {
    history.current = [...history.current.slice(-24), before];
    state.updateLineSet(next);
  };
  const set = state.deck.line_sets.find((item) => item.id === editing?.setId);
  const line = set?.entries.find((item) => item.id === editing?.lineId);
  const pickingSet = state.deck.line_sets.find((item) => item.id === picking?.setId);
  const pickingLine = pickingSet?.entries.find((item) => item.id === picking?.lineId);
  const update = (next: StudioLineEntry) => {
    if (set?.editable) commit(set, { ...set, entries: set.entries.map((item) => (item.id === next.id ? next : item)) });
  };
  const add = (target: StudioLineSet, kind: StudioLineEntry['kind']) => {
    const id = uuid();
    commit(target, {
      ...target,
      entries: [
        ...target.entries,
        { id, kind, label: '', ...(kind === 'calculation' ? { operator: 'sum' as const, references: [], weights: [] } : {}) }
      ]
    });
    // A new member line opens the hierarchy picker directly; other kinds open the settings modal.
    if (kind === 'member') setPicking({ setId: target.id, lineId: id });
    else setEditing({ setId: target.id, lineId: id });
  };
  /**
   * Inserts a new line ABOVE or BELOW an existing one, then opens it for editing.
   *
   * `add` only appends, so building a statement meant adding at the bottom and clicking the move
   * arrows up through dozens of rows. The new line inherits the neighbour's indent, because a row
   * inserted next to a detail line is almost always another detail line at the same depth.
   */
  const insert = (target: StudioLineSet, index: number, kind: StudioLineEntry['kind'], where: 'above' | 'below') => {
    if (!target.editable) return;
    const id = uuid();
    const at = where === 'above' ? index : index + 1;
    const neighbour = target.entries[index];
    const entry: StudioLineEntry = {
      id,
      kind,
      label: '',
      ...(kind === 'spacer' ? {} : { indent: neighbour?.indent ?? 0 }),
      // Same dimension as the neighbour: a line inserted beside a Cost Center row is another one.
      ...(kind === 'member' && neighbour?.binding_id ? { binding_id: neighbour.binding_id } : {}),
      ...(kind === 'calculation' ? { operator: 'sum' as const, references: [], weights: [] } : {})
    };
    commit(target, { ...target, entries: [...target.entries.slice(0, at), entry, ...target.entries.slice(at)] });
    // Same hand-off as `add`: a member line goes straight to the hierarchy, anything else to settings.
    if (kind === 'member') setPicking({ setId: target.id, lineId: id });
    else setEditing({ setId: target.id, lineId: id });
  };
  const remove = (target: StudioLineSet, id: string) => commit(target, { ...target, entries: target.entries.filter((item) => item.id !== id) });
  // Row presentation, editable per line from the shelf itself. Takes the target set explicitly
  // rather than the modal's cursor, so bolding or indenting a row does not mean opening it first.
  const setFormatting = (target: StudioLineSet, id: string, patch: Partial<StudioLineEntry>) => {
    if (!target.editable) return;
    commit(target, { ...target, entries: target.entries.map((item) => (item.id === id ? { ...item, ...patch } : item)) });
  };
  const toggleBold = (target: StudioLineSet, entry: StudioLineEntry) =>
    setFormatting(target, entry.id, { emphasis: entry.emphasis === 'bold' ? 'normal' : 'bold' });
  // Clamped: the deck's own rows sit at 1-3, and an unbounded indent just pushes a label out of view.
  const nudgeIndent = (target: StudioLineSet, entry: StudioLineEntry, offset: number) =>
    setFormatting(target, entry.id, { indent: Math.min(MAX_LINE_INDENT, Math.max(0, (entry.indent ?? 0) + offset)) });
  /**
   * The same formatting patch across MANY lines, in ONE write.
   *
   * Applying `setFormatting` per id would queue several `updateLineSet` calls against the same stale
   * `target`, so the last one would win and the rest would be lost.
   */
  const setFormattingMany = (target: StudioLineSet, ids: readonly string[], patch: Partial<StudioLineEntry>) => {
    if (!target.editable || ids.length === 0) return;
    const wanted = new Set(ids);
    commit(target, {
      ...target,
      entries: target.entries.map((item) => (wanted.has(item.id) ? { ...item, ...patch } : item))
    });
  };
  /** Bold if any selected line is not bold yet, else clear it — one predictable toggle for a mixed set. */
  const toggleBoldMany = (target: StudioLineSet, ids: readonly string[]) => {
    const wanted = new Set(ids);
    const selected = target.entries.filter((item) => wanted.has(item.id) && item.kind !== 'spacer');
    if (selected.length === 0) return;
    const allBold = selected.every((item) => item.emphasis === 'bold');
    setFormattingMany(
      target,
      selected.map((item) => item.id),
      { emphasis: allBold ? 'normal' : 'bold' }
    );
  };
  /** Indent/outdent many, each clamped on its OWN depth so a mixed selection keeps its relative shape. */
  const nudgeIndentMany = (target: StudioLineSet, ids: readonly string[], offset: number) => {
    if (!target.editable || ids.length === 0) return;
    const wanted = new Set(ids);
    commit(target, {
      ...target,
      entries: target.entries.map((item) =>
        wanted.has(item.id) && item.kind !== 'spacer'
          ? { ...item, indent: Math.min(MAX_LINE_INDENT, Math.max(0, (item.indent ?? 0) + offset)) }
          : item
      )
    });
  };
  const removeMany = (target: StudioLineSet, ids: readonly string[]) => {
    if (!target.editable || ids.length === 0) return;
    const wanted = new Set(ids);
    commit(target, { ...target, entries: target.entries.filter((item) => !wanted.has(item.id)) });
  };
  const move = (target: StudioLineSet, index: number, offset: number) =>
    commit(target, { ...target, entries: moveStudioItem(target.entries, index, offset) });
  // Apply a multi-selection from the hierarchy: the edited line takes the first member and each
  // additional member becomes its own line row inserted right after it. No selection clears it.
  const applyMembers = (memberIds: readonly string[]) => {
    if (!pickingSet?.editable || !pickingLine) return setPicking(undefined);
    const index = pickingSet.entries.findIndex((item) => item.id === pickingLine.id);
    const rows: StudioLineEntry[] = memberIds.length
      ? memberIds.map((memberId, i) =>
          i === 0
            ? { ...pickingLine, kind: 'member', member_id: memberId }
            : // The extra lines came from the SAME hierarchy, so they carry the edited line's dimension.
              {
                id: uuid(),
                kind: 'member',
                label: '',
                member_id: memberId,
                ...(pickingLine.binding_id ? { binding_id: pickingLine.binding_id } : {})
              }
        )
      : [{ ...pickingLine, member_id: undefined }];
    commit(pickingSet, {
      ...pickingSet,
      entries: [...pickingSet.entries.slice(0, index), ...rows, ...pickingSet.entries.slice(index + 1)]
    });
    return setPicking(undefined);
  };
  return {
    /** Which lines head others, whether each is open and shows an expand icon in the report, and which a closed node hides. */
    expansion: (target: StudioLineSet) => {
      const hidden = hiddenLineIds(target);
      return {
        nodes: lineNodeIds(target.entries),
        hidden,
        visible: target.entries.filter((entry) => !hidden.has(entry.id)),
        isExpandable: (entry: StudioLineEntry) => isLineExpandable(target, entry),
        isExpanded: (entry: StudioLineEntry) => isLineExpanded(target, entry)
      };
    },
    toggleLineExpanded: (target: StudioLineSet, entry: StudioLineEntry) =>
      commit(target, withLineExpanded(target, entry, !isLineExpanded(target, entry))),
    toggleLineExpandable: (target: StudioLineSet, entry: StudioLineEntry) =>
      commit(target, withLineExpandable(target, entry, !isLineExpandable(target, entry))),
    set,
    line,
    editing,
    setEditing,
    picking,
    setPicking,
    pickingSet,
    pickingLine,
    applyMembers,
    update,
    add,
    insert,
    /** True while there is an edit to take back. */
    canUndo: history.current.length > 0,
    /**
     * Restores the set as it was before the last edit.
     *
     * Does NOT snapshot, so Undo cannot be undone into a loop; pressing it repeatedly walks back through
     * the edits instead.
     */
    undo: () => {
      const previous = history.current[history.current.length - 1];
      if (!previous) return;
      history.current = history.current.slice(0, -1);
      state.updateLineSet(previous);
    },
    setFormattingMany,
    toggleBoldMany,
    nudgeIndentMany,
    removeMany,
    remove,
    move,
    setFormatting,
    toggleBold,
    nudgeIndent
  };
};
